import { CSSProperties } from "react";
import type { RecipeAwardTier } from "@/lib/store";

/**
 * AwardBadge
 *
 * Care-given chip shown on RecipeCard and RecipeDetail. Frames the metric as
 * "has nourished N sisters" — never "likes" or "cooks". The tier label is
 * tonal, the count is the proof. Tiers map to the brand palette and lovedCount
 * thresholds defined in store.ts.
 *
 *   none             0 to 4         no chip rendered
 *   sprouted         5+ sisters     fiber green
 *   rooted           25+            forest
 *   flourishing      100+           gold
 *   garden-favorite  500+           coral (Kiley's color)
 */

const TIER_META: Record<
  Exclude<RecipeAwardTier, "none">,
  { label: string; bg: string; fg: string; border: string; icon: string }
> = {
  sprouted: {
    label: "Sprouted",
    bg: "var(--mnf-fiberBg)",
    fg: "#2C6248",
    border: "var(--mnf-fiberBorder)",
    icon: "\ud83c\udf31",
  },
  rooted: {
    label: "Rooted",
    bg: "rgba(30,74,53,0.10)",
    fg: "var(--mnf-fd)",
    border: "rgba(30,74,53,0.18)",
    icon: "\ud83c\udf3f",
  },
  flourishing: {
    label: "Flourishing",
    bg: "var(--mnf-gold2)",
    fg: "var(--mnf-gold)",
    border: "rgba(160,120,32,0.24)",
    icon: "\u2728",
  },
  "garden-favorite": {
    label: "Garden Favorite",
    bg: "rgba(192,120,74,0.14)",
    fg: "var(--mnf-t)",
    border: "rgba(192,120,74,0.32)",
    icon: "\ud83c\udf3a",
  },
};

export default function AwardBadge({
  tier,
  count,
  size = "md",
  showCount = true,
  style,
  testId,
}: {
  tier: RecipeAwardTier;
  count: number;
  size?: "sm" | "md";
  showCount?: boolean;
  style?: CSSProperties;
  testId?: string;
}) {
  if (tier === "none") return null;
  const meta = TIER_META[tier];
  const fontSize = size === "sm" ? 10 : 11;
  const iconSize = size === "sm" ? 11 : 13;
  const padY = size === "sm" ? 3 : 4;
  return (
    <span
      data-testid={testId ?? `badge-award-${tier}`}
      className="inline-flex items-center gap-1.5 mnf-body"
      style={{
        background: meta.bg,
        color: meta.fg,
        border: `1px solid ${meta.border}`,
        padding: `${padY}px 9px`,
        borderRadius: 999,
        fontSize,
        fontWeight: 700,
        letterSpacing: "0.04em",
        ...style,
      }}
    >
      <span aria-hidden style={{ fontSize: iconSize, lineHeight: 1 }}>{meta.icon}</span>
      <span style={{ textTransform: "uppercase", letterSpacing: "0.08em" }}>{meta.label}</span>
      {showCount && (
        <span style={{ opacity: 0.75, fontWeight: 600, textTransform: "none", letterSpacing: 0 }}>
          · nourished {formatCount(count)}
        </span>
      )}
    </span>
  );
}

function formatCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return String(n);
}
